/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './TooltipStyles.scss';
/**
 * Check if an HTML element type is naturally focusable
 */
function isHTMLElementFocusable(type) {
    if (typeof type !== 'string')
        return false;
    const focusableElements = ['a', 'button', 'input', 'textarea', 'select', 'details'];
    return focusableElements.includes(type.toLowerCase());
}
/**
 * Tooltip component that displays contextual information on hover
 * Based on: https://www.figma.com/design/ZCRzifGUbiPZwWVO6jRD58/Quimby---UI--Web-?node-id=2257-560
 *
 * @example
 * ```tsx
 * <Tooltip content="Click to edit">
 *   <button>Edit</button>
 * </Tooltip>
 * ```
 */
export function Tooltip({ content, children, position = 'top', delay = 200, multiline = false }) {
    var _a, _b;
    const [isVisible, setIsVisible] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [timeoutId, setTimeoutId] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({
        top: 0,
        left: 0,
        position
    });
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);
    const tooltipId = useMemo(() => `qmb-ui-tooltip-${Math.random().toString(36).substr(2, 9)}`, []);
    const calculatePosition = () => {
        if (!containerRef.current || !tooltipRef.current)
            return;
        const container = containerRef.current.getBoundingClientRect();
        const tooltip = tooltipRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        let bestPosition = position;
        let top = 0;
        let left = 0;
        switch (position) {
            case 'top':
                top = container.top + scrollY - tooltip.height - 8;
                left = container.left + scrollX + container.width / 2 - tooltip.width / 2;
                break;
            case 'bottom':
                top = container.bottom + scrollY + 8;
                left = container.left + scrollX + container.width / 2 - tooltip.width / 2;
                break;
            case 'left':
                top = container.top + scrollY + container.height / 2 - tooltip.height / 2;
                left = container.left + scrollX - tooltip.width - 8;
                break;
            case 'right':
                top = container.top + scrollY + container.height / 2 - tooltip.height / 2;
                left = container.right + scrollX + 8;
                break;
        }
        // Auto-adjust if tooltip goes outside viewport
        if (top < scrollY) {
            bestPosition = 'bottom';
            top = container.bottom + scrollY + 8;
        }
        else if (top + tooltip.height > scrollY + viewportHeight) {
            bestPosition = 'top';
            top = container.top + scrollY - tooltip.height - 8;
        }
        if (left < scrollX) {
            left = scrollX + 8;
        }
        else if (left + tooltip.width > scrollX + viewportWidth) {
            left = scrollX + viewportWidth - tooltip.width - 8;
        }
        setTooltipPosition({ top, left, position: bestPosition });
    };
    const handleMouseEnter = () => {
        const id = setTimeout(() => {
            setIsVisible(true);
            setIsPositioned(false);
            requestAnimationFrame(() => {
                calculatePosition();
                setIsPositioned(true);
            });
        }, delay);
        setTimeoutId(id);
    };
    const handleMouseLeave = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
        setIsVisible(false);
        setIsPositioned(false);
    };
    const renderTooltipContent = () => {
        return content;
    };
    const tooltipElement = isVisible && content ? (React.createElement("div", { ref: tooltipRef, id: tooltipId, className: `qmb-ui-tooltip qmb-ui-tooltip--${tooltipPosition.position}${multiline ? ' qmb-ui-tooltip--multiline' : ''}`, style: {
            position: 'absolute',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 10010,
            opacity: isPositioned ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out'
        }, role: "tooltip", "aria-live": "polite" }, renderTooltipContent())) : null;
    // Prepare trigger element with focus handlers and aria-describedby
    const triggerElement = React.isValidElement(children) ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    React.cloneElement(children, {
        onFocus: handleMouseEnter,
        onBlur: handleMouseLeave,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'aria-describedby': isVisible ? tooltipId : undefined,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        tabIndex: (_b = (_a = children.props) === null || _a === void 0 ? void 0 : _a.tabIndex) !== null && _b !== void 0 ? _b : (isHTMLElementFocusable(children.type) ? undefined : 0)
    })) : (React.createElement("span", { role: "button", tabIndex: 0, onFocus: handleMouseEnter, onBlur: handleMouseLeave, "aria-describedby": isVisible ? tooltipId : undefined }, children));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: containerRef, className: "qmb-ui-tooltip__container", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }, triggerElement),
        isVisible && createPortal(tooltipElement, document.body)));
}
